"use client"

import type React from "react"

import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import { useEffect, useState } from "react"
import UserQuestionnaireBox from "../components/QuestionnaireBox"
import { type IUser, defaultUser } from "../../../../shared/models/User"
import { ChevronDown, ChevronUp, User, Building2, Briefcase, Heart, Users } from "lucide-react"

export let initials = ""

const UserValuesView = observer(() => {
  const { api, store } = useAppContext()
  const me = store.auth.meJson
  const [ratees, setRatees] = useState<IUser>({ ...defaultUser })
  const [isDetailsShown, setIsDetailsShown] = useState(false)
  const [disableAfterSubmission, setDisableAfterSubmission] = useState(false)

  useEffect(() => {
    const load = async () => {
      await Promise.all([
        api.value.getAll(),
        api.ratingAssignments.getAll(),
        api.department.getAll(),
        api.user.getAll(),
      ])
    }
    load()
  }, [api])

  const values = store.value.all.map((value) => value.asJson)
  const users = store.user.all.map((user) => user.asJson)
  const departments = store.department.all.map((department) => department.asJson)

  const activeRatingAssignment = store.ratingAssignments.all.find((r) => r.asJson.isActive)

  const rateAssignments = activeRatingAssignment ? activeRatingAssignment.asJson.ratedUsersPerAssignmentValues : {}

  const matches = store.ratingAssignments.all
    .filter((assignment) => assignment.asJson.isActive)
    .flatMap((assignment) => assignment.asJson.feedbackAssignments[me?.uid ?? ""])

  const matchingUsers = users
    .filter((user) => matches.includes(user.uid))
    .map((user) => ({
      value: user.uid,
      label: `${user.displayName} ${user.lastName}`,
    }))

  const alreadyRatedUserIds = rateAssignments[me?.uid ?? ""] || []

  const actualUsers = matchingUsers.filter((user) => !alreadyRatedUserIds.includes(user.value))

  const name = ratees.displayName || " "
  initials = name
    .split(" ")
    .map((namePart) => namePart[0])
    .join("")

  const getDepartmentName = (departmentId: string): string => {
    const department = departments.find((dep) => dep.id === departmentId)
    return department ? department.name : "Unknown Department"
  }

  const handleRaterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUser = users.find((user) => user.uid === e.target.value)
    if (selectedUser) setRatees(selectedUser)
  }

  const toggleDetails = () => {
    setIsDetailsShown((prev) => !prev)
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-100/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
            <Heart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Values Assessment</h2>
            <p className="text-slate-600">Evaluate core organizational values</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-lg">
            <span className="text-slate-700">Hi</span>
            <span className="font-bold text-blue-600">{me?.displayName}</span>
            <span className="text-slate-700">
              , you are about to provide a rating for Values. Please select a user:
            </span>
          </div>

          <div className="relative max-w-xs">
            <select
              onChange={handleRaterChange}
              value={ratees.uid || ""}
              required
              className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer text-slate-700 font-medium"
            >
              <option value="">Select User</option>
              {actualUsers.map((user, index) => (
                <option key={index} value={user.value}>
                  {user.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
          </div>

          {/* Selected User Info */}
          {ratees.uid && (
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/50">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-xl font-bold text-white">{initials}</span>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <User className="h-3 w-3 text-white" />
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-slate-600">You are rating</p>
                    <p className="text-lg font-bold text-slate-800">{ratees.displayName}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-700">
                      <Building2 className="h-4 w-4 text-blue-600" />
                      <span>{getDepartmentName(ratees.department)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700">
                      <Briefcase className="h-4 w-4 text-purple-600" />
                      <span>{ratees.jobTitle}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Already Rated Users */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">Users Already Rated</h3>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
              {alreadyRatedUserIds.length}
            </span>
          </div>
          <button onClick={toggleDetails} className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200">
            {isDetailsShown ? (
              <ChevronUp className="h-5 w-5 text-slate-600" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-600" />
            )}
          </button>
        </div>

        {isDetailsShown && (
          <div className="space-y-2">
            {alreadyRatedUserIds.length > 0 ? (
              alreadyRatedUserIds.map((userId) => (
                <div key={userId} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-slate-700 font-medium">
                    {users.find((user) => user.uid === userId)?.displayName || "Unknown User"}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">No users rated yet</p>
            )}
          </div>
        )}
      </div>

      {/* Questionnaire */}
      <UserQuestionnaireBox
        value={values}
        ratee={ratees.uid}
        description={activeRatingAssignment?.asJson.description || ""}
        disableAfterSubmission={disableAfterSubmission}
        setRatees={setRatees}
      />
    </div>
  )
})

export default UserValuesView
